import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { QueryAdderCell } from './QueryAdderCell';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { TrackerDataType } from '../../shared/constants/constants';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import {
  bulkUpdateContentLogs,
  bulkUpdateEngagements,
  bulkUpdateInteractions,
  bulkUpdateServices,
} from '../../shared/requests/bulkUpdateRequests';
import { getBulkEditQueryAdderColumnsByEventHandlerType } from '../../shared/constants/IssueAndServiceKeys';
import findOrganizationFromContact from '../../shared/functions/findOrganizationFromContact';
import { normalizeToIds } from '../../utils/eventHandlerUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

const sanitizeBulkEditFields = (fields) => {
  for (const key in fields) {
    if (Object.hasOwn(fields, key)) {
      const value = fields[key];
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete fields[key];
      }
    }
  }
  return fields;
};

const getDetailQueryKey = (trackerType) => {
  switch (trackerType) {
    case TrackerDataType.MEDIA_INTERACTION:
      return 'issueDetail';
    case TrackerDataType.SERVICE:
      return 'serviceDetail';
    case TrackerDataType.ENGAGEMENT:
      return 'engagementDetail';
    case TrackerDataType.CONTENT_LOG:
      return 'contentLogDetail';
    default:
      return null;
  }
};

export default function BulkEditQueryAdder({
  selectedBulkEditRowIds,
  eventHandler,
  dropdownTable,
  currMonth,
  rowType,
  onClose,
}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { state } = useLocation();

  const columns = getBulkEditQueryAdderColumnsByEventHandlerType(eventHandler.type);
  const initialBulkEditFieldsState = { entryDate: null, status: undefined };

  const [isSubmitBulkEditButtonLoading, setIsSubmitBulkEditButtonLoading] = useState(false);
  const [bulkEditFields, setBulkEditFields] = useState(initialBulkEditFieldsState);

  const getActionButtonColor = () => {
    switch (eventHandler.type) {
      case TrackerDataType.MEDIA_INTERACTION:
        return 'mediaInteraction';
      case TrackerDataType.ENGAGEMENT:
        return 'primaryLighter';
      case TrackerDataType.CONTENT_LOG:
        return 'primaryLight';
      default:
        return 'primary';
    }
  };

  const handleCloseBulkEditPopup = () => {
    onClose();
    setBulkEditFields(initialBulkEditFieldsState);
  };

  const bulkEditMutation = useMutation({
    mutationFn: ({ request, rowIds, fields }) => request(rowIds, fields),
    onMutate: () => {
      setIsSubmitBulkEditButtonLoading(true);
    },
    onSuccess: async (_data, variables) => {
      dispatch(openSnackbar({ message: `Edit multiple ${variables.snackbarType} success`, severity: 'success' }));
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      await queryClient.invalidateQueries({ queryKey: [variables.invalidateQueryKey, currMonth] });
      if (variables.filterInvalidateKey) {
        await queryClient.invalidateQueries({ queryKey: [variables.filterInvalidateKey] });
      }
      const detailQueryKey = getDetailQueryKey(eventHandler.type);
      if (detailQueryKey) {
        await queryClient.invalidateQueries({ queryKey: [detailQueryKey] });
        await Promise.all(
          variables.rowIds.flatMap((rowId) => {
            const rowIdVariants = new Set([rowId, String(rowId)]);
            return [...rowIdVariants].map((rowIdVariant) =>
              queryClient.invalidateQueries({ queryKey: [detailQueryKey, rowIdVariant] })
            );
          })
        );
      }
      if (state?.queryString && state?.category) {
        await queryClient.invalidateQueries({
          queryKey: ['fuzzySearch', eventHandler.type, state.queryString, state.category],
        });
      }
      handleCloseBulkEditPopup();
    },
    onError: () => {
      handleCloseBulkEditPopup();
      dispatch(openSnackbar({ message: 'Edit failed, please try again', severity: 'error' }));
    },
    onSettled: () => {
      setIsSubmitBulkEditButtonLoading(false);
    },
  });

  const handleSubmitBulkEdit = () => {
    try {
      let bulkEditRequest;
      let snackbarType;
      let invalidateQueryKey;
      let filterInvalidateKey;
      switch (eventHandler.type) {
        case TrackerDataType.MEDIA_INTERACTION:
          bulkEditRequest = bulkUpdateInteractions;
          snackbarType = 'interactions';
          invalidateQueryKey = 'monthlyIssues';
          filterInvalidateKey = 'filteredIssues';
          break;
        case TrackerDataType.SERVICE:
          bulkEditRequest = bulkUpdateServices;
          snackbarType = 'services';
          invalidateQueryKey = 'monthlyServices';
          filterInvalidateKey = 'filteredServices';
          break;
        case TrackerDataType.ENGAGEMENT:
          bulkEditRequest = bulkUpdateEngagements;
          snackbarType = 'engagements';
          invalidateQueryKey = 'monthlyEngagements';
          filterInvalidateKey = 'filteredEngagements';
          break;
        case TrackerDataType.CONTENT_LOG:
          bulkEditRequest = bulkUpdateContentLogs;
          snackbarType = 'content logs';
          invalidateQueryKey = 'monthlyContentLogs';
          filterInvalidateKey = 'filteredContentLogs';
          break;
        default:
          throw new Error('unknown event handler type');
      }

      const preparedFields = { ...bulkEditFields };
      const idMappings = [
        { source: 'communityMember', target: 'communityMembers' },
        { source: 'organization', target: 'organizations' },
      ];
      for (const { source, target } of idMappings) {
        const rawValue = preparedFields[source];
        if (!rawValue) {
          continue;
        }

        const ids = normalizeToIds(rawValue);
        delete preparedFields[source];
        if (ids.length) {
          preparedFields[target] = ids;
        }
      }

      bulkEditMutation.mutate({
        request: bulkEditRequest,
        rowIds: selectedBulkEditRowIds,
        fields: sanitizeBulkEditFields(preparedFields),
        snackbarType,
        invalidateQueryKey,
        filterInvalidateKey,
      });
    } catch (err) {
      handleCloseBulkEditPopup();
      dispatch(openSnackbar({ message: 'Edit failed, please try again', severity: 'error' }));
    }
  };

  const normalizeValues = (input) => {
    if (Array.isArray(input)) {
      return input;
    }
    if (input) {
      return [input];
    }
    return [];
  };

  const handleBulkEditFieldsChange = (val, col) => {
    let newField;
    let autoOrganizationsToAdd = null;
    let organizationIdsAfterChange = null;

    switch (col) {
      case 'organization': {
        const organizations = normalizeValues(val);
        newField = organizations;
        organizationIdsAfterChange = organizations
          .map((organization) => organization?._id ?? organization)
          .filter(Boolean);
        break;
      }
      case 'communityMember': {
        const communityMembers = normalizeValues(val);
        newField = communityMembers;

        if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
          const organizationLookup = dropdownTable.organization || [];
          autoOrganizationsToAdd = communityMembers
            .map((communityMember) => {
              const organizationRef = communityMember?.Organization;
              if (!organizationRef) return null;

              if (typeof organizationRef === 'string') {
                return organizationLookup.find((organization) => organization._id === organizationRef) ?? null;
              }
              if (organizationRef?._id) {
                return (
                  organizationLookup.find((organization) => organization._id === organizationRef._id) ?? organizationRef
                );
              }
              if (organizationRef?.id) {
                return organizationLookup.find((organization) => organization._id === organizationRef.id) ?? null;
              }
              return null;
            })
            .filter(Boolean);
        }
        break;
      }
      case 'journalist':
      case 'expert':
        newField = val;
        break;
      case 'audience':
        newField = normalizeValues(val);
        break;
      case 'outlet':
        newField = val?._id;
        break;
      case 'teamMember':
        newField = val;
        break;
      case 'topic':
      case 'response':
      case 'lead':
      case 'type':
      case 'date':
      case 'entryDate':
      case 'publishDate':
      case 'dueDate':
      case 'service':
      case 'unitLead':
      case 'notes':
      case 'background':
      case 'content':
      case 'otherNotes':
      case 'keyMessaging':
      case 'actions':
      case 'tone':
      case 'linkedInteraction':
      case 'linkedEngagement':
      case 'complexity':
      case 'status':
        newField = val;
        break;
      case 'campaign':
        newField = val.map((campaign) => {
          return campaign;
        });
        break;
      case 'units':
        newField = val.map((unit) => {
          return unit;
        });
        break;
      case 'dept':
        newField = val.map((dept) => {
          return dept?.name;
        });
        break;
      default:
        newField = val?.name;
        break;
    }
    setBulkEditFields((prevBulkEditFieldsState) => ({ ...prevBulkEditFieldsState, [col]: newField }));

    if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
      if (autoOrganizationsToAdd) {
        setBulkEditFields((prev) => {
          const existingOrganizations = Array.isArray(prev.organization) ? [...prev.organization] : [];
          for (const organization of autoOrganizationsToAdd) {
            if (
              organization &&
              !existingOrganizations.some((existingOrganization) => existingOrganization._id === organization._id)
            ) {
              existingOrganizations.push(organization);
            }
          }
          return { ...prev, organization: existingOrganizations };
        });
      }

      if (organizationIdsAfterChange) {
        setBulkEditFields((prev) => {
          if (!Array.isArray(prev.communityMember)) {
            return prev;
          }

          const filteredCommunityMembers = prev.communityMember.filter((communityMember) => {
            const organizationRef = communityMember?.Organization;
            if (!organizationRef) return true;

            let organizationId = null;
            if (typeof organizationRef === 'string') {
              organizationId = organizationRef;
            } else if (organizationRef?._id) {
              organizationId = organizationRef._id;
            } else if (organizationRef?.id) {
              organizationId = organizationRef.id;
            }

            if (!organizationId) return true;
            if (organizationIdsAfterChange.length === 0) return false;

            return organizationIdsAfterChange.includes(organizationId);
          });

          return { ...prev, communityMember: filteredCommunityMembers };
        });
      }
    }

    if (col === 'outlet') {
      setBulkEditFields((prevBulkEditFieldsState) => ({ ...prevBulkEditFieldsState, journalist: null }));
    } else if (col === 'journalist') {
      setBulkEditFields((prevBulkEditFieldsState) => ({
        ...prevBulkEditFieldsState,
        outlet: findOrganizationFromContact(newField, dropdownTable['outlet'], 'Org'),
      }));
    } else if (col === 'unitLead' || col === 'expert') {
      const currentSelectedExperts = Array.isArray(newField) ? newField : [];

      const unitsFromSelectedExperts = currentSelectedExperts
        .map((expert) => {
          if (!expert || !expert.unit) {
            return null;
          }
          if (!dropdownTable || !dropdownTable['units'] || !Array.isArray(dropdownTable['units'])) {
            return null;
          }
          const unitObj = dropdownTable['units'].find((u) => u._id === expert.unit);
          return unitObj ? unitObj.name : null;
        })
        .filter(Boolean);

      setBulkEditFields((prevBulkEditFieldsState) => {
        const existingUnits =
          prevBulkEditFieldsState.units && Array.isArray(prevBulkEditFieldsState.units)
            ? prevBulkEditFieldsState.units
            : [];
        const combinedUnits = [...new Set([...existingUnits, ...unitsFromSelectedExperts])];
        return {
          ...prevBulkEditFieldsState,
          units: combinedUnits,
        };
      });
    }
  };

  const isEmptyFields = useMemo(() => {
    if (Object.keys(bulkEditFields).length === 0) {
      return true;
    }

    for (const key in bulkEditFields) {
      const value = bulkEditFields[key];
      if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        return false;
      }
    }

    return true;
  }, [bulkEditFields]);

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Box>
          <Typography align="center" fontWeight={500} fontSize="24px">
            Edit {selectedBulkEditRowIds.length} {rowType}
          </Typography>
        </Box>
        <Box>
          <LoadingButton
            sx={{ width: '220px', height: '40px', fontSize: '16px', mx: '10px', fontWeight: 550 }}
            color={getActionButtonColor()}
            variant="contained"
            loading={isSubmitBulkEditButtonLoading}
            disabled={isEmptyFields}
            onClick={() => handleSubmitBulkEdit()}
            data-cy="bulkEditQueryAdderSubmitButton"
          >
            Submit
          </LoadingButton>
          <Button
            sx={{ width: '220px', height: '40px', fontSize: '16px', mx: '10px', fontWeight: 550 }}
            width={200}
            color={getActionButtonColor()}
            variant="outlined"
            onClick={() => handleCloseBulkEditPopup()}
            data-cy="bulkEditQueryAdderCancelButton"
          >
            Cancel
          </Button>
        </Box>
      </Stack>
      <Divider sx={{ borderColor: 'black', my: '10px' }} />
      <Alert sx={{ mb: '5px' }} severity="error">
        By clicking Submit, any filled fields will be overwritten for {selectedBulkEditRowIds.length} {rowType}.
      </Alert>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px 5px',
          alignItems: 'center',
          overflowY: 'scroll',
          justifyContent: 'space-between',
          '& > label': {
            width: '100%',
          },
        }}
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmitBulkEdit();
        }}
      >
        {columns.map((col) => (
          <QueryAdderCell
            key={col}
            col={col}
            dropdownTable={dropdownTable}
            fields={bulkEditFields}
            callback={handleBulkEditFieldsChange}
            required={false}
            error={false}
            eventHandler={eventHandler}
            isBulkUpdate={true}
          />
        ))}
      </Box>
    </>
  );
}

BulkEditQueryAdder.propTypes = {
  selectedBulkEditRowIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  eventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]).isRequired,
  }).isRequired,
  dropdownTable: PropTypes.shape({
    organization: PropTypes.array,
    outlet: PropTypes.array,
    units: PropTypes.array,
  }).isRequired,
  currMonth: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  rowType: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};
